import type { AiConversation, AiMessage } from "@/entities/ai/types"

function csrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

const headers = () => ({
  "Content-Type": "application/json",
  "X-CSRF-Token": csrfToken(),
})

export class AiNotConfiguredError extends Error {
  constructor() {
    super("AI is not configured for this workspace")
    this.name = "AiNotConfiguredError"
  }
}

export async function createConversation(workspaceSlug: string): Promise<AiConversation> {
  const res = await fetch(`/api/${workspaceSlug}/ai/conversations`, {
    method: "POST",
    headers: headers(),
  })
  if (!res.ok) {
    if (res.status === 422) {
      const body = await res.json().catch(() => null)
      if (body?.error?.includes("not configured")) {
        throw new AiNotConfiguredError()
      }
    }
    throw new Error("Failed to create conversation")
  }
  return res.json()
}

export async function getConversations(workspaceSlug: string): Promise<AiConversation[]> {
  const res = await fetch(`/api/${workspaceSlug}/ai/conversations`, { headers: headers() })
  if (!res.ok) throw new Error("Failed to fetch conversations")
  return res.json()
}

export async function sendMessage(
  workspaceSlug: string,
  conversationId: number,
  content: string
): Promise<AiMessage> {
  const res = await fetch(
    `/api/${workspaceSlug}/ai/conversations/${conversationId}/messages`,
    {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ message: { content } }),
    }
  )
  if (!res.ok) throw new Error("Failed to send message")
  return res.json()
}

export async function getMessages(
  workspaceSlug: string,
  conversationId: number
): Promise<AiMessage[]> {
  const res = await fetch(
    `/api/${workspaceSlug}/ai/conversations/${conversationId}/messages`,
    { headers: headers() }
  )
  if (!res.ok) throw new Error("Failed to fetch messages")
  return res.json()
}
